import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Button, {ButtonProps, ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import ButtonGroup from 'ui-library/buttons/ButtonGroup';
import Popover from 'ui-library/floating-ui/Popover';

export interface ToggleGroupOption<T extends React.Key> {
  id: T;
  dataTestAttrs?: DataTestAttrs;
  buttonProps: Omit<ButtonProps, 'buttonStyle' | 'onClick' | 'size'>;
  /*
   * Caution. Setting popover on a button will affect the button styling.
   * - There are double borderlines between buttons with popover and their neighbors.
   * - If not every button within the group has a popover, those popover-less buttons
   *   will have non-zero radius, which look slightly detached from the group.
   */
  popOverProps?: {disabled: boolean; text: string};
  disabled?: boolean;
}

export interface ToggleGroupProps<T extends React.Key> {
  disabled?: boolean;
  options: Array<ToggleGroupOption<T>>;
  selection: T;
  onChangeSelection: (selection: T) => void;
  size: ButtonSize;
  isFullWidth: boolean;
  wrap?: boolean;
}
ToggleGroup.defaultProps = {
  isFullWidth: false,
};

/**
 * Button-style picker for a number of options, presented as a button group with a blue selection and other
 * options gray.
 *
 * It's recommended to use an enum for the ID type.
 */
export default function ToggleGroup<T extends React.Key>({
  disabled,
  options,
  selection,
  onChangeSelection,
  size,
  isFullWidth,
  wrap,
}: ToggleGroupProps<T>): JSX.Element {
  return (
    <ButtonGroup isFullWidth={isFullWidth} dataTestAttrs={{component: 'ToggleGroup', element: 'root'}}>
      {options.map((option) => (
        <Popover
          key={option.id}
          renderContent={() => <div className={css(styles.popoverText)}>{option.popOverProps?.text}</div>}
          isDisabled={option.popOverProps?.disabled ?? true}
        >
          <Button
            key={option.id}
            disabled={disabled || option.disabled}
            className={classNames({
              [css(styles.selectedItem)]: option.id === selection,
              [css(styles.unselectedItem)]: option.id !== selection,
              [css(styles.wrap)]: wrap,
            })}
            {...option.buttonProps}
            buttonStyle={
              option.id === selection ? ButtonStyle.TERTIARY_BLUE_POWDER : ButtonStyle.TERTIARY_WHITE
            }
            onClick={() => {
              onChangeSelection(option.id);
            }}
            size={size}
            dataTestAttrs={
              option.dataTestAttrs ?? {component: 'ToggleGroup', element: 'button', key: `${option.id}`}
            }
          />
        </Popover>
      ))}
    </ButtonGroup>
  );
}

const styles = StyleSheet.create({
  popoverText: {
    textAlign: 'center',
    maxWidth: '200px',
  },
  wrap: {
    height: 'auto',
  },
  selectedItem: {
    zIndex: 2,
  },
  unselectedItem: {
    zIndex: 1,
  },
});
